'use client';

import { X } from 'lucide-react';
import { Dialog as RadixDialog } from 'radix-ui';
import type { ReactNode } from 'react';
import { Button } from './button';
import { cn } from './cn';
import { IconButton } from './icon-button';

/**
 * Where focus lands when a dialog opens: what asks for it (`data-autofocus`), else the first field,
 * else anything focusable in the body, else the first footer button (« Annuler » in a confirmation,
 * so Enter never confirms a deletion), else the dialog itself. Never the close button: its tooltip
 * would open and take the first Escape.
 */
export function focusFirstControl(event: Event) {
  const content = event.currentTarget;
  if (!(content instanceof HTMLElement)) return;
  event.preventDefault();
  // Plain selector lists, no :is(): jsdom, used by the projects' tests, does not parse it.
  const inBody = (controls: string[]) =>
    controls.map((control) => `[data-dialog-body] ${control}:not(:disabled)`).join(', ');
  const target =
    content.querySelector<HTMLElement>('[data-autofocus]') ??
    content.querySelector<HTMLElement>(
      inBody(['input:not([type=hidden])', 'textarea', 'select', 'button[role="combobox"]']),
    ) ??
    content.querySelector<HTMLElement>(
      inBody(['button', '[href]', '[tabindex]:not([tabindex="-1"])']),
    ) ??
    content.querySelector<HTMLElement>('[data-dialog-footer] button:not(:disabled)');
  (target ?? content).focus();
}

const widths = {
  sm: 'w-[min(420px,calc(100vw-2rem))]',
  md: 'w-[min(520px,calc(100vw-2rem))]',
  lg: 'w-[min(720px,calc(100vw-2rem))]',
} as const;

/** The backdrop: it dims the page. */
export const dialogOverlayClass =
  'fixed inset-0 z-[60] bg-black/40 data-[state=closed]:animate-crow-overlay-out data-[state=open]:animate-crow-overlay-in';

/**
 * The window's surface (elevation 3) and motion, without its layout, for a project that lays its
 * dialogs out itself (crowdex). Its frame (border and corners) shows from `sm` up.
 */
export const dialogSurfaceClass =
  'bg-crow-surface text-crow-text shadow-crow-3 outline-none data-[state=closed]:animate-crow-dialog-out data-[state=open]:animate-crow-dialog-in sm:rounded-crow-window sm:border sm:border-crow-line';

/** A modal window: elevation 3, the backdrop dims the page. Full screen on phones. */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = 'md',
  dismissible = true,
  className,
  bodyClassName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  /** Buttons, bottom right; the main action goes last. */
  footer?: ReactNode;
  size?: keyof typeof widths;
  /** False while an action runs: Escape, the backdrop and the close button do nothing. */
  dismissible?: boolean;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className={dialogOverlayClass} />
        <RadixDialog.Content
          onOpenAutoFocus={focusFirstControl}
          onEscapeKeyDown={(event) => {
            if (!dismissible) event.preventDefault();
          }}
          onInteractOutside={(event) => {
            if (!dismissible) event.preventDefault();
          }}
          // Centred by margins, not by a transform: a transform would become the containing block
          // of fixed-position popovers rendered inside the dialog.
          className={cn(
            'fixed inset-0 z-[60] flex flex-col overflow-y-auto sm:m-auto sm:h-fit sm:max-h-[85vh]',
            dialogSurfaceClass,
            widths[size],
            'max-sm:w-auto',
            className,
          )}
        >
          <div className="flex items-start gap-4 p-5 pb-0">
            <div className="min-w-0 flex-1">
              <RadixDialog.Title className="font-crow-heading text-crow-title font-semibold">
                {title}
              </RadixDialog.Title>
              {description ? (
                <RadixDialog.Description className="mt-1 text-crow-body text-crow-text-2">
                  {description}
                </RadixDialog.Description>
              ) : (
                <RadixDialog.Description className="sr-only">{title}</RadixDialog.Description>
              )}
            </div>
            <RadixDialog.Close asChild>
              <IconButton
                label="Fermer"
                size="icon-sm"
                disabled={!dismissible}
                className="-mt-1 -mr-1"
              >
                <X />
              </IconButton>
            </RadixDialog.Close>
          </div>
          {children ? (
            <div data-dialog-body className={cn('flex-1 p-5', bodyClassName)}>
              {children}
            </div>
          ) : (
            <div className="h-5" />
          )}
          {footer ? (
            <div
              data-dialog-footer
              className="sticky bottom-0 flex flex-wrap justify-end gap-2 border-t border-crow-line bg-crow-surface px-5 py-3"
            >
              {footer}
            </div>
          ) : null}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

/**
 * For irreversible actions only: name the object and the consequence. Anything that can be undone
 * deserves a notification with « Annuler » instead.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Annuler',
  danger = false,
  pending = false,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  danger?: boolean;
  pending?: boolean;
  onConfirm: () => void;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {cancelLabel}
          </Button>
          <Button
            variant={danger ? 'danger-solid' : 'primary'}
            pending={pending}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </>
      }
    />
  );
}
